import { ChangeEvent, KeyboardEvent, FocusEvent, useState } from 'react'
import styled from 'styled-components'
import { Chip, FormControl, TextField, TextFieldProps } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'

const CustomTexField = styled(TextField)`
  & label.Mui-focused {
    color: #142954;
  }
  & .MuiOutlinedInput-root {
    &.Mui-focused fieldset {
      border-color: #142954;
    }
  }
`

const CustomClearIcon = styled(ClearIcon)`
  position: absolute;
  right: 1rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: filter 0.2s;
  background-color: #fff;
  color: rgba(0, 0, 0, 0.54);
  font-size: 2.3rem;
  border-radius: 50%;

  &:hover {
    filter: brightness(0.95);
  }
`

interface FormChipsInputProps extends Omit<TextFieldProps, 'onChange'> {
  labelName?: string
  name: string
  value: string[]
  onChange: (name: string, value: string[]) => void
}

function FormChipsInput({
  name,
  labelName,
  value,
  onChange,
  ...rest
}: FormChipsInputProps) {
  const [currentValue, setCurrentValue] = useState('')

  const addTags = () => {
    const results = currentValue.split(' ').filter((item) => item)
    setCurrentValue('')
    onChange(name, [...value, ...results])
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const { key } = event

    const handlePressEnter = () => {
      addTags()

      const shouldPreventDefault =
        !value.length || (value.length && currentValue.length)
      shouldPreventDefault && event.preventDefault()
    }

    const handlePressBackspace = () => {
      onChange(name, value.slice(0, -1))
    }

    const keys: Record<string, () => void> = {
      Enter: handlePressEnter,
      Tab: handlePressEnter,
      Backspace: handlePressBackspace,
      default: () => null
    }

    const handler = keys[key] ?? keys.default
    handler()
  }

  const handleTagDelete = (i: number) => () => {
    onChange(
      name,
      value.filter((_, index) => index !== i)
    )
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(event.target.value)
  }

  const handleClearAll = () => {
    onChange(name, [])
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    addTags()
  }

  return (
    <FormControl fullWidth>
      <CustomTexField
        label={labelName}
        value={currentValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        FormHelperTextProps={{
          style: {
            fontSize: '12px'
          }
        }}
        style={{ position: 'relative' }}
        InputProps={{
          startAdornment:
            !!value?.length &&
            value?.map((item: any, i: number) => (
              <Chip key={i} label={item} onDelete={handleTagDelete(i)} />
            )),
          endAdornment: !!value?.length && (
            <CustomClearIcon onClick={handleClearAll} />
          ),
          style: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            padding: !!value?.length ? '1rem 1rem 0 1rem' : ''
          },
          inputProps: {
            style: {
              fontSize: '14px'
            }
          }
        }}
        InputLabelProps={{
          style: {
            fontSize: '14px'
          }
        }}
        {...rest}
      />
    </FormControl>
  )
}

export default FormChipsInput
